import { Component, ElementRef, OnInit, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { AuthService } from '../service/auth/auth.service';
import { LoggerService } from '../service/utils/logger.service';
import { RuntimeConfigService } from '../service/config/runtime-config.service';
import {
  AtplnestBillingPlan,
  RevenueCatService,
} from '../service/billing/revenuecat.service';
import { PurchasesError } from '@revenuecat/purchases-js';
import { BrandService } from '../service/brand/brand.service';

@Component({
  selector: 'app-subscriptions',
  standalone: true,
  imports: [RouterLink, CommonModule],
  templateUrl: './subscriptions.component.html',
  styleUrl: './subscriptions.component.css',
})
export class SubscriptionsComponent implements OnInit {
  @ViewChild('rcPaywallHost') rcPaywallHost?: ElementRef<HTMLElement>;
  protected readonly brand = inject(BrandService);

  rcLoading = false;
  rcError = '';
  rcInfoMessage = '';
  rcProActive: boolean | null = null;
  rcAppUserId: string | null = null;
  rcOfferingSummary = '';
  managementUrl: string | null = null;
  renewalDate: string | null = null;
  willRenew: boolean | null = null;

  constructor(
    private authService: AuthService,
    public runtime: RuntimeConfigService,
    public revenueCat: RevenueCatService,
  ) {}

  get rcEnabled(): boolean {
    return this.brand.showBilling && !!this.runtime.revenueCatPublicApiKey?.trim();
  }

  async ngOnInit() {
    if (!this.brand.showBilling) {
      return;
    }
    await this.refreshRevenueCat();
  }

  async refreshRevenueCat(): Promise<void> {
    this.rcError = '';
    this.rcInfoMessage = '';
    if (!this.rcEnabled) {
      this.rcProActive = null;
      this.rcAppUserId = null;
      this.rcOfferingSummary = '';
      this.managementUrl = null;
      this.renewalDate = null;
      this.willRenew = null;
      return;
    }
    const user = this.authService.getUser();
    await this.revenueCat.ensureIdentified(user);
    if (!this.revenueCat.isConfigured) {
      this.rcProActive = null;
      return;
    }
    this.rcLoading = true;
    try {
      this.rcAppUserId = user?.id != null ? this.revenueCat.appUserIdFor(user.id) : null;
      const entitled = await this.revenueCat.isEntitledToPro();
      this.rcProActive = entitled;
      const customerInfo = await this.revenueCat.getCustomerInfo();
      this.managementUrl = this.revenueCat.managementUrl(customerInfo);
      const entitlement = customerInfo?.entitlements?.active?.[this.revenueCat.entitlementId] as {
        expirationDate?: string | null;
        willRenew?: boolean;
      } | undefined;
      this.renewalDate = entitlement?.expirationDate ?? null;
      this.willRenew = typeof entitlement?.willRenew === 'boolean' ? entitlement.willRenew : null;
      const offerings = await this.revenueCat.getOfferings();
      const cur = offerings?.current;
      this.rcOfferingSummary = cur
        ? `${cur.identifier} (${cur.availablePackages?.length ?? 0} packages)`
        : '—';
    } catch (e: unknown) {
      LoggerService.error('[RevenueCat] refresh failed', e);
      this.rcError = this.formatRcError(e);
    } finally {
      this.rcLoading = false;
    }
  }

  async openPaywall(): Promise<void> {
    this.rcError = '';
    this.rcInfoMessage = '';
    const host = this.rcPaywallHost?.nativeElement;
    if (!host) {
      this.rcError = 'Paywall container not ready.';
      return;
    }
    const user = this.authService.getUser();
    await this.revenueCat.ensureIdentified(user);
    if (!this.revenueCat.isConfigured) {
      this.rcError = 'RevenueCat is not configured. Add revenueCatPublicApiKey to runtime-config.';
      return;
    }
    this.rcLoading = true;
    try {
      host.innerHTML = '';
      const info = await this.revenueCat.presentPaywall({
        htmlTarget: host,
        customerEmail: user?.email || undefined,
      });
      if (info) {
        this.rcInfoMessage = 'Purchase or paywall flow completed. Refreshing status…';
        await this.revenueCat.pushCustomerInfoToBackend(user);
      }
      await this.refreshRevenueCat();
    } catch (e: unknown) {
      if (RevenueCatService.isUserCancelled(e)) {
        this.rcInfoMessage = 'Checkout was cancelled.';
      } else {
        this.rcError = this.formatRcError(e);
      }
    } finally {
      this.rcLoading = false;
    }
  }

  async purchasePlan(plan: AtplnestBillingPlan): Promise<void> {
    this.rcError = '';
    this.rcInfoMessage = '';
    const user = this.authService.getUser();
    await this.revenueCat.ensureIdentified(user);
    if (!this.revenueCat.isConfigured) {
      this.rcError = 'RevenueCat is not configured.';
      return;
    }
    const host = this.rcPaywallHost?.nativeElement;
    this.rcLoading = true;
    try {
      const info = await this.revenueCat.purchasePlan(plan, {
        htmlTarget: host,
        customerEmail: user?.email || undefined,
        appUserId: user?.id != null ? this.revenueCat.appUserIdFor(user.id) : undefined,
      });
      if (info) {
        this.rcInfoMessage = 'Thank you — subscription updated.';
        await this.revenueCat.pushCustomerInfoToBackend(user);
      }
      await this.refreshRevenueCat();
    } catch (e: unknown) {
      if (RevenueCatService.isUserCancelled(e)) {
        this.rcInfoMessage = 'Purchase cancelled.';
      } else {
        this.rcError = this.formatRcError(e);
      }
    } finally {
      this.rcLoading = false;
    }
  }

  private formatRcError(e: unknown): string {
    if (e instanceof PurchasesError) {
      const msg = e.message;
      return msg ? msg : 'RevenueCat error';
    }
    if (e instanceof Error) {
      return e.message;
    }
    return 'Unexpected error';
  }

}
